import * as React from 'react';
import { isElement } from '@floating-ui/utils/dom';
import { useEventCallback } from '@base-ui-components/utils/useEventCallback';
import { useId } from '@base-ui-components/utils/useId';
import { createEventEmitter } from "../utils/createEventEmitter.js";
import { useFloatingParentNodeId } from "../components/FloatingTree.js";
export function useFloatingRootContext(options) {
  const {
    open = false,
    onOpenChange: onOpenChangeProp,
    elements: elementsProp
  } = options;
  const floatingId = useId();
  const dataRef = React.useRef({});
  const [events] = React.useState(() => createEventEmitter());
  const nested = useFloatingParentNodeId() != null;
  if (process.env.NODE_ENV !== 'production') {
    const optionDomReference = elementsProp.reference;
    if (optionDomReference && !isElement(optionDomReference)) {
      console.error('Cannot pass a virtual element to the `elements.reference` option,', 'as it must be a real DOM element. Use `refs.setPositionReference()`', 'instead.');
    }
  }
  const [positionReference, setPositionReference] = React.useState(elementsProp.reference);
  const onOpenChange = useEventCallback((newOpen, eventDetails) => {
    dataRef.current.openEvent = newOpen ? eventDetails.event : undefined;
    if (!options.noEmit) {
      const details = {
        open: newOpen,
        reason: eventDetails.reason,
        nativeEvent: eventDetails.event,
        nested
      };
      events.emit('openchange', details);
    }
    onOpenChangeProp?.(newOpen, eventDetails);
  });
  const refs = React.useMemo(() => ({
    setPositionReference
  }), []);
  const elements = React.useMemo(() => ({
    reference: positionReference || elementsProp.reference || null,
    floating: elementsProp.floating || null,
    domReference: elementsProp.reference
  }), [positionReference, elementsProp.reference, elementsProp.floating]);
  return React.useMemo(() => ({
    dataRef,
    open,
    onOpenChange,
    elements,
    events,
    floatingId,
    refs
  }), [open, onOpenChange, elements, events, floatingId, refs]);
}
export function getEmptyContext() {
  return {
    open: false,
    onOpenChange: () => {},
    dataRef: {
      current: {}
    },
    elements: {
      floating: null,
      reference: null,
      domReference: null
    },
    events: {
      on: () => {},
      off: () => {},
      emit: () => {}
    },
    floatingId: '',
    refs: {
      setPositionReference: () => {}
    }
  };
}